import type {
  CSSProperties,
  KeyboardEvent,
  MutableRefObject,
  ReactElement,
  RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import type { SelectOption } from './Select';

interface SelectListProps {
  id: string;
  listRef: RefObject<HTMLUListElement | null>;
  optionRefs: MutableRefObject<(HTMLLIElement | null)[]>;
  options: readonly SelectOption[];
  value: string;
  label: string | undefined;
  position: CSSProperties;
  onKeyDown: (e: KeyboardEvent<HTMLUListElement>) => void;
  onChoose: (index: number) => void;
}

/**
 * Lista de opciones del Select, dibujada en el <body> con posición fija (ningún
 * contenedor con scroll la recorta). El clic se delega en la lista, donde también vive
 * el teclado.
 */
export function SelectList({
  id,
  listRef,
  optionRefs,
  options,
  value,
  label,
  position,
  onKeyDown,
  onChoose,
}: SelectListProps): ReactElement {
  return createPortal(
    <ul
      ref={listRef}
      id={id}
      role="listbox"
      className="pop-menu sel-list"
      style={position}
      aria-label={label}
      onKeyDown={onKeyDown}
      onClick={(e) => {
        const option = (e.target as HTMLElement).closest<HTMLElement>('[role="option"]');
        if (option?.dataset['index'] !== undefined) {
          onChoose(Number(option.dataset['index']));
        }
      }}
    >
      {options.map((o, i) => (
        <li
          key={o.value}
          ref={(el) => {
            optionRefs.current[i] = el;
          }}
          role="option"
          tabIndex={-1}
          className="pop-item"
          aria-selected={o.value === value}
          aria-disabled={o.disabled === true || undefined}
          data-index={i}
        >
          {o.label}
        </li>
      ))}
    </ul>,
    document.body,
  );
}
